import { AccountTreeRounded, CloseRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, IconButton, InputAdornment, Paper, Radio, RadioGroup, Stack, Step, StepLabel, Stepper, TextField, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { IAncestorFlow } from '../../../definitions/Nodes'
import { IMultipleFlowsConfigs } from '../../../definitions/NodesExecution'
import useCommand from '../../hooks/useCommand'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import { snackbar } from '../../services/SnackbarService'
import ProgressStep from './ProgressStep'

interface P {
    setOpenModal: (openStartRunModal: boolean) => void
    setBlockUI: (blockUI: boolean) => void
    selectedRows: string[]
    config?: IMultipleFlowsConfigs
    runMode?: 'full' | 'dry'
    refetch?: () => Promise<void>
    ancestor?: {
        ancestorUUID?: string
        ancestorName?: string
    }
}

const DATE_FORMAT = 'YYYY-MM-DD'
const NOW = moment()

const requiredText = (label: string, width?: string) => (
    <Stack flexDirection='row' gap={0.5} width={width ?? undefined}>
        <Typography variant='subtitle2' fontWeight={500}>{label}</Typography>
        <Typography variant='subtitle2' fontWeight={500} color='error'>*</Typography>
    </Stack>

)

const RunModal = ({ setOpenModal, setBlockUI, selectedRows, config = null, refetch = null, ancestor }: P) => {
    const [activeStep, setActiveStep] = useState(0)
    const [lastStepCompleted, setLastStepCompleted] = useState(false)
    const [runMode, setRunMode] = useState<'full' | 'dry'>('dry')
    const [runSetting, setRunSetting] = useState<IMultipleFlowsConfigs>(config ?? { settlementEngine: { ancestorUUID: ancestor?.ancestorUUID, ancestorName: ancestor?.ancestorName }, from: NOW.clone().month(0).day(1).format(DATE_FORMAT), to: NOW.clone().format(DATE_FORMAT), settlementInstance: 'Interim' })
    const [ytd, setYtd] = useState(config?.to ? moment(config.to, 'YYYY-MM-DD') : NOW)
    const [enabledCustomDate, setEnabledCustomDate] = useState(false)
    const [forceEngine, setForceEngine] = useState(config?.forceEngine ?? false)
    const [selectedVersion, setSelectedVersion] = useState<IAncestorFlow['children'][0]>(null)
    const [execEnvUUIDs, setExecEnvUUIDs] = useState<string[]>([])

    const { status, response } = useQuery({
        queryKey: buildQueryKey('getAncestorEngines', runSetting?.settlementEngine?.ancestorUUID),
        queryFn: () => FlowApiClient.getAncestorEngines(runSetting?.settlementEngine?.ancestorUUID),
        cache: false,
        enabled: !!runSetting?.settlementEngine?.ancestorUUID
    })

    useEffect(() => {
        if (config && response) {
            const found = response.children.find(r => r.uuid === config.settlementEngine.uuid)
            setSelectedVersion(found ?? null)
        }
    }, [response, config])

    const { execute: startMultipleFlows, status: statusStartMultipleFlows } = useCommand<{ contractUUIDs: string[], runConfigs: IMultipleFlowsConfigs, runMode: 'full' | 'dry' }, string[]>({
        commandFn: async ({ contractUUIDs, runConfigs, runMode }) => {
            const execEnvUUIDs = await FlowApiClient.startMultipleFlows(contractUUIDs, runConfigs, runMode)

            await FlowApiClient.createSettlementResult(contractUUIDs, execEnvUUIDs, runConfigs)

            const batchSize = 1
            const delayBatchMs = 1500

            for (let i = 0; i < execEnvUUIDs.length; i += batchSize) {
                const batch = execEnvUUIDs.slice(i, i + batchSize)

                for (const UUIDs of batch) {
                    FlowApiClient.executeFlow(UUIDs, true)
                }

                if (i + batchSize < execEnvUUIDs.length) {
                    await new Promise(res => setTimeout(res, delayBatchMs))
                }
            }

            return execEnvUUIDs
        },
        onFinish: async result => {
            if (result.status === 'success') {
                setActiveStep(2)
                setBlockUI(true)
                setExecEnvUUIDs(result.response)
            }
            if (result.status === 'error') {
                snackbar.show({ severity: 'error', text: result.errorText })
            }
        }
    })

    const handleCloseModal = useCallback(() => {
        setOpenModal(false)
    }, [])

    const handleBackStep = useCallback(() => {
        setActiveStep(prev => prev - 1)
    }, [])

    const handleNextStep = useCallback(() => {
        setActiveStep(prev => prev + 1)
    }, [])

    const handleFieldChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target

        if (name === 'instance' && (value === 'Interim' || value === 'Final')) {
            setRunSetting(prev => ({ ...prev, settlementInstance: value }))
        }
        if (name === 'sample') setRunSetting(prev => ({ ...prev, samplePercentage: Number(value) }))
        if (name === 'seed') setRunSetting(prev => ({ ...prev, overrideSeed: Number(value) }))
        if (name === 'output') setRunSetting(prev => ({ ...prev, generateOutputs: value === 'true' }))
    }, [])

    const handleYtdDateChange = (value: Moment | null) => {
        if (!value?.isValid()) {
            setYtd(null)
            return
        }

        if (value.isAfter(moment(), 'month')) {
            return
        }

        setYtd(value)

        const selectedYear = value.year()
        const selectedMonth = value.month()

        const fromDate = moment().year(selectedYear).month(0).date(1)

        const isCurrentMonth = selectedYear === NOW.year() && selectedMonth === NOW.month()

        const toDate = isCurrentMonth
            ? moment()
            : value.clone().endOf('month')

        setRunSetting(prev => ({
            ...prev,
            from: fromDate.format(DATE_FORMAT),
            to: toDate.format(DATE_FORMAT)
        }))
    }

    const handleCustomDateChange = (value: Moment | null, period: 'from' | 'to') => {
        if (!value?.isValid()) {
            setRunSetting(prev => ({
                ...prev,
                ...(period === 'from' ? { from: '' } : { to: '' })
            }))
            return
        }
        setRunSetting(prev => ({
            ...prev,
            ...(period === 'from' ? { from: value.format(DATE_FORMAT) } : { to: value.format(DATE_FORMAT) })
        }))
    }

    const handleExecute = useCallback(async () =>
        await startMultipleFlows({ contractUUIDs: selectedRows, runConfigs: runSetting, runMode }),
    [selectedRows, runSetting, runMode]
    )

    const rightColor = runMode === 'dry' ? 'dryRun' : 'primary'
    const stepStyles = {
        '.MuiStepIcon-root.Mui-completed': {
            color: 'var(--color-okay)'
        },
        '.MuiStepIcon-root.Mui-active': {
            color: theme => runMode === 'dry' ? theme.palette.dryRun.main : undefined
        },
        '.MuiStepIcon-root.Mui-active.Mui-completed': { color: 'var(--color-okay)' }
    }

    const loading = status === 'loading' || status === 'stale'

    return (
        <Paper sx={{ display: 'flex', flexDirection: 'column', width: 800, maxHeight: 800 }}>
            <Stack spacing={3}>
                <Stack flexDirection='row' justifyContent='space-between' paddingX={2} paddingTop={2}>
                    <Stack flexDirection='row' alignItems='center' gap={2}>
                        <AccountTreeRounded color={rightColor} />
                        <Stack>
                            <Typography variant='h6' fontWeight={500}>
                                {(activeStep === 0 || activeStep === 1) && 'Settlement run configuration'}
                                {activeStep === 2 && 'Settlement run in progress'}
                            </Typography>
                            <Typography variant='subtitle2' fontWeight={500}>
                                {(activeStep === 0 || activeStep === 1) && 'Set the settlement date range and instance for this run.'}
                                {activeStep === 2 && 'Status updates automatically. Your results will be available when processing completes'}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Box>
                        <IconButton size='small' onClick={handleCloseModal}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Box>
                </Stack>
                <Stack sx={{ backgroundColor: theme => theme.palette.background.default, width: '100%', paddingY: 2, alignItems: 'center' }}>
                    <Stepper activeStep={activeStep} sx={{ minWidth: '70%' }}>
                        <Step sx={stepStyles}>
                            <StepLabel>Engine config</StepLabel>
                        </Step>
                        <Step sx={stepStyles}>
                            <StepLabel>Run config</StepLabel>
                        </Step>
                        <Step sx={stepStyles} completed={lastStepCompleted}>
                            <StepLabel>Settlement calculation</StepLabel>
                        </Step>
                    </Stepper>
                </Stack>
                {activeStep === 0 && (
                    <>
                        <Stack paddingX={2}>
                            <Typography color={theme => theme.palette.text.secondary} variant='subtitle2'>Used engine:</Typography>
                            <Typography>{loading ? 'Loading ancestor name...' : response?.ancestorName}</Typography>
                        </Stack>
                        <Stack flexDirection='row' alignItems='center' gap={2.5} paddingX={2}>
                            <Autocomplete
                                size='small'
                                disableClearable
                                sx={{ width: 300 }}
                                loading={false}
                                disabled={!forceEngine}
                                options={response?.children ?? []}
                                value={selectedVersion}
                                getOptionLabel={o => `${o.name} (v${o.version}) ${o.isHead ? ' - approved' : ''}`}
                                isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
                                onChange={(_, newValue) => {
                                    setSelectedVersion(newValue)
                                    setRunSetting(prev => ({
                                        ...prev,
                                        settlementEngine: {
                                            ...prev.settlementEngine,
                                            uuid: newValue.uuid
                                        }
                                    }))
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label='Used version'
                                        placeholder={!forceEngine ? 'Default' : ''}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading && <CircularProgress color='inherit' size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size='small'
                                        checked={forceEngine}
                                        onChange={e => {
                                            setForceEngine(e.target.checked)
                                            setSelectedVersion(null)
                                            setRunSetting(prev => ({
                                                ...prev,
                                                forceEngine: e.target.checked,
                                                settlementEngine: {
                                                    ...prev.settlementEngine,
                                                    uuid: null
                                                }
                                            }))
                                        }}
                                    />
                                )}
                                label='Force all contracts to use the same engine'
                            />
                        </Stack>
                    </>
                )}
                {activeStep === 1 && (
                    <>
                        <Stack spacing={1.5} paddingX={2}>
                            <Typography variant='subtitle2'>Settlement instance type</Typography>
                            <FormControl>
                                <RadioGroup
                                    row
                                    name='instance'
                                    value={runSetting.settlementInstance}
                                    onChange={handleFieldChange}>
                                    <FormControlLabel value='Interim' control={<Radio size='small' color={rightColor} />} label='Interim' />
                                    <FormControlLabel value='Final' control={<Radio size='small' color={rightColor} />} label='Final' />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                        <Stack paddingX={2}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Stack spacing={2} width='80%'>
                                    {requiredText('Year to Date selection')}
                                    <Stack flexDirection='row' alignItems='center' gap={2}>
                                        <DatePicker
                                            disabled={enabledCustomDate}
                                            label='YTD'
                                            openTo='year'
                                            views={['year', 'month']}
                                            disableFuture
                                            maxDate={NOW}
                                            onChange={handleYtdDateChange}
                                            slotProps={{ textField: { size: 'small', error: ytd === null && !enabledCustomDate, InputLabelProps: { shrink: true }, color: rightColor } }}
                                            value={ytd}
                                        />
                                        <Box>
                                            <FormControlLabel
                                                control={(
                                                    <Checkbox
                                                        size='small'
                                                        checked={enabledCustomDate}
                                                        onChange={() => setEnabledCustomDate(prev => !prev)}
                                                    />
                                                )}
                                                label='Select the precise dates instead'
                                            />
                                        </Box>
                                    </Stack>
                                    {requiredText('Settlement\'s date')}
                                    <Stack flexDirection='row' gap={2} alignItems='center'>
                                        <DatePicker
                                            disabled={!enabledCustomDate}
                                            disableFuture
                                            maxDate={NOW}
                                            label='From'
                                            openTo='day'
                                            views={['year', 'month', 'day']}
                                            onChange={v => handleCustomDateChange(v, 'from')}
                                            slotProps={{ textField: { size: 'small', error: runSetting.from === '', InputLabelProps: { shrink: true }, color: rightColor } }}
                                            value={moment(runSetting.from, 'YYYY-MM-DD')}
                                        />
                                        <Typography variant='body1'>—</Typography>
                                        <DatePicker
                                            disabled={!enabledCustomDate}
                                            disableFuture
                                            maxDate={NOW}
                                            label='To'
                                            openTo='day'
                                            views={['year', 'month', 'day']}
                                            onChange={v => handleCustomDateChange(v, 'to')}
                                            slotProps={{ textField: { size: 'small', error: runSetting.to === '', InputLabelProps: { shrink: true }, color: rightColor } }}
                                            value={moment(runSetting.to, 'YYYY-MM-DD')}
                                        />
                                    </Stack>
                                </Stack>
                            </LocalizationProvider>
                        </Stack>
                        {runMode === 'dry' && (
                            <>
                                <Divider />
                                <Stack paddingX={2} spacing={1.5}>
                                    <Typography variant='subtitle1'>Dry Run Configuration</Typography>
                                    <Stack flexDirection='row' alignItems='center' gap={3} mt={2}>
                                        <Stack width='33%' spacing={1.5}>
                                            {requiredText('Sample')}
                                            <TextField
                                                name='sample'
                                                color={rightColor}
                                                fullWidth
                                                size='small'
                                                error={isNaN(runSetting.samplePercentage)}
                                                InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                                                InputLabelProps={{ shrink: true }}
                                                type='number'
                                                value={runSetting.samplePercentage}
                                                onChange={handleFieldChange}
                                            />
                                        </Stack>
                                        <Stack width='33%' spacing={1.5}>
                                            <Typography variant='subtitle2'>Seed</Typography>
                                            <TextField
                                                name='seed'
                                                color={rightColor}
                                                fullWidth
                                                size='small'
                                                error={isNaN(runSetting?.overrideSeed ?? 0)}
                                                InputLabelProps={{ shrink: true }}
                                                type='number'
                                                value={runSetting?.overrideSeed ?? ''}
                                                onChange={handleFieldChange}
                                            />
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </>
                )}
                {activeStep === 2 && (
                    <ProgressStep setOpenModal={setOpenModal} execEnvUUIDs={execEnvUUIDs} runMode={runMode} config={runSetting} contractUUIDs={selectedRows} setLastStepCompleted={setLastStepCompleted} setBlockUI={setBlockUI} refetch={refetch} />
                )}
                <Stack flexDirection='row' justifyContent='end' alignItems='center' gap={1.5} paddingX={2} paddingBottom={2}>
                    {activeStep === 0 && (
                        <Stack flexDirection='row' alignItems='center' gap={1.5}>
                            <Button
                                size='small'
                                variant='text'
                                color='error'
                                onClick={handleCloseModal}>cancel
                            </Button>
                            <Button
                                size='small'
                                variant='contained'
                                // @ts-ignore
                                disabled={selectedVersion?.approvalStatus !== 'approved'}
                                onClick={() => {
                                    setRunMode('full')
                                    handleNextStep()
                                }}>Full run
                            </Button>
                            <Button
                                size='small'
                                variant='contained'
                                color='dryRun'
                                onClick={() => {
                                    setRunMode('dry')
                                    setRunSetting(prev => ({
                                        ...prev,
                                        generateOutputs: true,
                                        samplePercentage: 100
                                    }))
                                    handleNextStep()
                                }}>Dry run
                            </Button>
                        </Stack>
                    )}
                    {activeStep === 1 && (
                        <Stack flexDirection='row' alignItems='center' gap={1.5}>
                            <Button
                                size='small'
                                variant='text'
                                color='error'
                                onClick={handleBackStep}
                                disabled={statusStartMultipleFlows === 'loading'}>
                                Back
                            </Button>
                            <LoadingButton
                                loading={statusStartMultipleFlows === 'loading'}
                                size='small'
                                variant='contained'
                                color={rightColor}
                                disabled={(ytd === null && !enabledCustomDate) || runSetting.from === '' || runSetting.to === '' || (runMode === 'dry' && isNaN(runSetting.samplePercentage))}
                                onClick={handleExecute}>
                                {runMode === 'full' ? 'Start Full Run' : 'Start Dry Run'}
                            </LoadingButton>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Paper>
    )
}

export default memo(RunModal)
